/**
 * Module dependencies.
 */

import { Text, baseFontSize, clampCalc } from 'src/components/core/text';
import { TourData } from 'src/types/tour';
import { ifProp } from 'styled-tools';
import { media } from 'src/styles/media';
import { useRouter } from 'next/router';
import FadeInAnimation from 'src/components/core/animations/fade-in';
import FadeInRightAnimation from 'src/components/core/animations/fade-in-right';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import Image from 'src/components/core/image';
import VideoPlayer from 'src/components/video-player';
import isEmpty from 'lodash/isEmpty';
import styled, { css } from 'styled-components';
import useBreakpoint from 'src/hooks/use-breakpoint';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = Partial<TourData['sections']['guest']>;

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  display: grid;
  grid-template-areas:
    '. titleWrapper .'
    '. authorImage  .'
    '. authorImage  .'
    '. .            .'
    '. quoteWrapper .';
  grid-template-columns: var(--gutter-mobile) 1fr var(--gutter-mobile);
  grid-template-rows: max-content 1fr 2fr 1.5fr max-content;
  height: 100%;
  margin-bottom: 5rem;

  ${media.min.md`
    grid-template-areas:
      '. .            .            . .           .           .'
      '. .            .            . authorImage authorImage .'
      '. titleWrapper titleWrapper . authorImage authorImage .'
      '. .            quoteWrapper . authorImage authorImage .'
      '. .            .            . .           .           .';
    grid-template-columns: 0.5fr 1fr 4fr 1fr 1fr 3fr 2.5fr;
    grid-template-rows: 8.5vw 3vw max-content 1fr 8.1vw;
    margin-bottom: 0;
    width: 100vw;

    @media screen and (max-height: 700px) {
      grid-template-columns: 0.5fr 1fr 4fr 1fr 1fr 3fr 2.5fr;
      grid-template-rows: 0vw 3vw max-content 1fr 8.1vw;
    }
  `}
`;

/**
 * `TitleWrapper` styled component.
 */

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: titleWrapper;
  margin-bottom: 2rem;
  position: relative;
  z-index: 3;

  ${media.min.md`
    margin-bottom: 4rem;
  `};
`;

/**
 * `EpisodeLabel` styled component.
 */

const EpisodeLabel = styled(Text).attrs({
  uppercase: true,
  variant: 'header'
})`
  color: var(--color-yellow);
  display: block;
  margin-bottom: 0.5rem;
  text-align: center;
  width: 100%;

  ${media.min.md`
    margin-bottom: 1rem;
    text-align: left;
  `};
`;

/**
 * `Title` styled component.
 */

const Title = styled(Text).attrs({
  as: 'h2',
  uppercase: true,
  variant: 'titleL'
})<{
  hasAfterTitle: boolean;
}>`
  color: var(--color-dark);
  font-size: clamp(${44 / baseFontSize}rem, ${clampCalc(44, 120)}, ${120 / baseFontSize}rem);
  line-height: clamp(${44 / baseFontSize}rem, ${clampCalc(44, 120)}, ${120 / baseFontSize}rem);
  margin-bottom: 1.5rem;
  z-index: 3;

  ${media.max.md`
    margin-bottom: 0.5rem;
    text-align: center;
  `}

  @media (min-width: 1920px) {
    font-size: ${155 / baseFontSize}rem;
    line-height: ${155 / baseFontSize}rem;
  }

  ${ifProp(
    'hasAfterTitle',
    css`
      margin-bottom: 0 !important;
    `
  )}
`;

/**
 * `AfterTitle` styled component.
 */

const AfterTitle = styled(Text).attrs({
  as: 'h3',
  uppercase: true,
  variant: 'titleXS'
})`
  color: var(--color-dark);
  margin-bottom: 1.5rem;

  ${media.max.md`
    text-align: center;
  `}
`;

/**
 * `Subtitle` styled component.
 */

const Subtitle = styled(Text).attrs({
  as: 'p',
  uppercase: true,
  variant: 'titleBoldM'
})`
  color: var(--color-dark);
  font-size: clamp(${16 / baseFontSize}rem, ${clampCalc(16, 20)}, ${20 / baseFontSize}rem);
  letter-spacing: 5px;
  line-height: clamp(${16 / baseFontSize}rem, ${clampCalc(16, 20)}, ${20 / baseFontSize}rem);
  margin: 0;
  position: relative;
  width: max-content;

  ${media.max.md`
    text-align: center;
    margin-bottom: 2rem;
    width: 100%;
  `}

  @supports (-webkit-hyphens: none) {
    ${media.max.md`
      margin-bottom: 0.5rem;
    `}
  }
`;

/**
 * `HandWrittenText` styled component.
 */

const HandWrittenText = styled(Text).attrs({ variant: 'handwrittenS' })`
  color: var(--color-yellow);
  margin: 0;
  text-transform: none;

  ${media.min.md`
    position: absolute;
    right: -120%;
  `}

  @supports (-webkit-hyphens: none) {
    font-size: clamp(40px, ${clampCalc(40, 42)}, 42px);
    position: relative;
    right: auto;
  }
`;

/**
 * `HandWrittenTextMobile` styled component.
 */

const HandWrittenTextMobile = styled(Text).attrs({ variant: 'handwrittenS' })`
  color: var(--color-yellow);
  display: block;
  margin-bottom: 4rem;
  text-align: center;

  @supports (-webkit-hyphens: none) {
    font-size: clamp(24px, ${clampCalc(24, 42)}, 42px);
  }
`;

/**
 * `QuoteWrapper` styled component.
 */

const QuoteWrapper = styled.div`
  align-self: end;
  display: flex;
  flex-direction: column;
  grid-area: quoteWrapper;
  margin-bottom: 2vw;
`;

/**
 * `Quote` styled component.
 */

const Quote = styled(Text).attrs({
  as: 'p',
  variant: 'quoteBlock'
})`
  color: var(--color-dark);
  margin: 0;

  ${media.min.md`
    margin-bottom: 4vw;
  `}

  ${media.max.md`
    margin-bottom: 1.25rem;
    text-align: center;
  `}
`;

/**
 * `ScrollText` styled component.
 */

const ScrollText = styled(Text).attrs({
  as: 'p',
  variant: 'header'
})`
  color: var(--color-grey-500);
  font-size: 0.938rem;
  letter-spacing: 0.047rem;
  line-height: 0.438rem;
  margin: 0;

  ${media.max.md`
    text-align: center;
  `};
`;

/**
 * `AuthorImage` styled component.
 */

const AuthorImage = styled.div`
  grid-area: authorImage;
  z-index: 2;

  ${media.min.md`
    align-items: center;
    display: grid;
  `};
`;

/**
 * `AuthorImageWrapper` styled component.
 */

const AuthorImageWrapper = styled.div`
  align-self: center;
  overflow: hidden;
  padding-bottom: 132%;
  position: relative;
`;

/**
 * `CoverImage` styled component.
 */

const CoverImage = styled.div`
  grid-column: 6 / -1;
  grid-row: 1 / -1;

  ${media.max.md`
    grid-column: 1 / -1;
    grid-row: 3 / 5;
    margin-bottom: 4rem;
  `};
`;

/**
 * `CoverImageWrapper` styled component.
 */

const CoverImageWrapper = styled.div`
  height: 100%;
  overflow: hidden;
  position: relative;

  ${media.max.md`
    padding-bottom: 135%;
  `};
`;

/**
 * `Intro` component.
 */

const Intro = (props: Props) => {
  const { assets, author, episode, quote, subTitle, titlePlace } = props;
  const { translate } = useTranslate();
  const isMobile = useBreakpoint('md', 'max');
  const { isReady } = useRouter();

  return (
    <Wrapper>
      {isReady && (
        <>
          <TitleWrapper>
            {!isMobile && (
              <FadeInRightAnimation
                options={{
                  transitionDelay: 0.5,
                  transitionTime: 1
                }}
              >
                <EpisodeLabel>{episode}</EpisodeLabel>
              </FadeInRightAnimation>
            )}

            {isMobile && (
              <FadeInUpAnimation
                options={{
                  transitionDelay: 0.5,
                  transitionTime: 1
                }}
              >
                <EpisodeLabel>{episode}</EpisodeLabel>
              </FadeInUpAnimation>
            )}

            {!isMobile && (
              <FadeInRightAnimation
                options={{
                  transitionDelay: 0.6,
                  transitionTime: 1
                }}
              >
                <Title hasAfterTitle={!isEmpty(subTitle)}>{titlePlace}</Title>

                {subTitle && <AfterTitle>{subTitle}</AfterTitle>}
              </FadeInRightAnimation>
            )}

            {isMobile && (
              <FadeInUpAnimation
                options={{
                  transitionDelay: 0.6,
                  transitionTime: 1
                }}
              >
                <Title hasAfterTitle={!isEmpty(subTitle)}>{titlePlace}</Title>

                {subTitle && <AfterTitle>{subTitle}</AfterTitle>}
              </FadeInUpAnimation>
            )}

            {!isMobile && (
              <FadeInRightAnimation
                options={{
                  transitionDelay: 0.7,
                  transitionTime: 1
                }}
              >
                <Subtitle>
                  {author?.by}

                  <HandWrittenText>{author?.surname}</HandWrittenText>
                </Subtitle>
              </FadeInRightAnimation>
            )}

            {isMobile && (
              <>
                <FadeInUpAnimation
                  options={{
                    transitionDelay: 0.7,
                    transitionTime: 1
                  }}
                >
                  <Subtitle>{author?.by}</Subtitle>
                </FadeInUpAnimation>

                <FadeInUpAnimation
                  options={{
                    distance: '100%',
                    transitionDelay: 0.8,
                    transitionTime: 1
                  }}
                >
                  <HandWrittenTextMobile>{author?.surname}</HandWrittenTextMobile>
                </FadeInUpAnimation>
              </>
            )}
          </TitleWrapper>

          <AuthorImage>
            {assets?.authorImage && (
              <FadeInAnimation
                options={{
                  transitionDelay: 0.6,
                  transitionTime: 1
                }}
              >
                <AuthorImageWrapper>
                  <Image alt={author?.fullName} layout={'fill'} objectFit={'cover'} src={assets?.authorImage} />
                </AuthorImageWrapper>
              </FadeInAnimation>
            )}
          </AuthorImage>

          <CoverImage>
            <FadeInAnimation
              fullHeight
              options={{
                transitionDelay: 0.7,
                transitionTime: 1
              }}
            >
              <CoverImageWrapper>
                {isMobile && assets?.authorCoverImage && (
                  <Image
                    alt={`${author?.fullName} background`}
                    layout={'fill'}
                    objectFit={'cover'}
                    src={assets?.authorCoverImage}
                  />
                )}

                {!isMobile && assets?.coverVideo && (
                  <VideoPlayer controls={false} loop muted playing url={assets?.coverVideo} />
                )}
              </CoverImageWrapper>
            </FadeInAnimation>
          </CoverImage>

          <QuoteWrapper>
            {!isMobile && (
              <FadeInRightAnimation
                options={{
                  transitionDelay: 0.8,
                  transitionTime: 1
                }}
              >
                <Quote>{quote}</Quote>

                <ScrollText>{translate('common:actions.scrollMore')}</ScrollText>
              </FadeInRightAnimation>
            )}

            {isMobile && (
              <FadeInUpAnimation options={{ transitionTime: 1 }}>
                <Quote>{quote}</Quote>

                <ScrollText>{translate('common:actions.scrollMore')}</ScrollText>
              </FadeInUpAnimation>
            )}
          </QuoteWrapper>
        </>
      )}
    </Wrapper>
  );
};

/**
 * Export `Intro` component.
 */

export default Intro;
